/**
 * Очистка URL от OAuth-параметров Keycloak (hash и query) после login/logout redirect.
 *
 * @module utils/keycloakAuthUrl
 */

import { ROUTE_HOME } from '../routes/routes.constants';

const KEYCLOAK_AUTH_CALLBACK_PARAM_KEYS = ['code', 'state', 'session_state'] as const;
const KEYCLOAK_AUTH_ERROR_PARAM_KEYS = ['error', 'error_description'] as const;

const getHashParams = (hash: string): URLSearchParams => {
  const normalizedHash = hash.startsWith('#') ? hash.slice(1) : hash;
  return new URLSearchParams(normalizedHash);
};

const hasKnownKeycloakAuthParams = (params: URLSearchParams): boolean => {
  for (const key of KEYCLOAK_AUTH_CALLBACK_PARAM_KEYS) {
    const value = params.get(key);
    if (typeof value === 'string' && value !== '') {
      return true;
    }
  }

  return false;
};

const getKnownKeycloakAuthError = (params: URLSearchParams): string | null => {
  const error = params.get('error');
  if (typeof error !== 'string' || error === '') {
    return null;
  }

  if (!hasKnownKeycloakAuthParams(params)) {
    return null;
  }

  return error;
};

/**
 * Удаляет из URL параметры OAuth-callback Keycloak (в query и hash).
 *
 * @param {string} urlString - исходный URL
 * @returns {string} URL без `code`/`state`/`session_state` в query и hash
 */
export function sanitizeKeycloakAuthUrl(urlString: string): string {
  try {
    const url = new URL(urlString);

    for (const key of KEYCLOAK_AUTH_CALLBACK_PARAM_KEYS) {
      url.searchParams.delete(key);
    }
    for (const key of KEYCLOAK_AUTH_ERROR_PARAM_KEYS) {
      url.searchParams.delete(key);
    }

    const hashParams = getHashParams(url.hash);
    if (hasKnownKeycloakAuthParams(hashParams) || getKnownKeycloakAuthError(hashParams) !== null) {
      url.hash = '';
    }

    return url.toString();
  } catch {
    return urlString;
  }
}

/**
 * Собирает post-logout redirect на корень survey-client с сохранением query gate-параметров.
 *
 * @param {string} appOrigin - базовый origin (`VITE_APP_URL`)
 * @param {URLSearchParams} searchParams - query текущей страницы
 * @returns {string} абсолютный URL без OAuth hash
 */
export function buildPostLogoutRedirectUri(appOrigin: string, searchParams: URLSearchParams): string {
  const url = new URL(ROUTE_HOME, appOrigin);
  const redirectUriParam = searchParams.get('redirect_uri');

  if (typeof redirectUriParam === 'string' && redirectUriParam !== '') {
    url.searchParams.set('redirect_uri', redirectUriParam);
  }

  const cmpImport = searchParams.get('cmp_import') || searchParams.get('cmpimport');
  if (typeof cmpImport === 'string' && cmpImport !== '') {
    url.searchParams.set('cmp_import', cmpImport);
  }

  return sanitizeKeycloakAuthUrl(url.toString());
}

/**
 * Заменяет текущий URL в history на версию без OAuth hash/query (после успешного login).
 *
 * @returns {void}
 */
export function cleanUrlAfterKeycloakAuth(): void {
  if (typeof window === 'undefined' || typeof window.history.replaceState !== 'function') {
    return;
  }

  const sanitizedUrl = sanitizeKeycloakAuthUrl(window.location.href);
  window.history.replaceState({}, '', sanitizedUrl);
}
